/*
 * ******************************************************************
 * Copyright (c) 2017-2025 Broadcom. All Rights Reserved.
 * Broadcom Confidential. The term "Broadcom" refers to Broadcom Inc.
 * and/or its subsidiaries.
 * ******************************************************************
 */

import { ChangeDetectorRef, Pipe, PipeTransform } from "@angular/core";
import { DecimalPipe } from "@angular/common";
import { L10nService, LocaleService } from "@vmw/ngx-vip";

/*
 * Converts bytes to largest possible unit with default precision of 2.
 * Usage:
 *   bytes | fileSize:precision
 * Example:
 *   {{ 1024 |  fileSize }} formats to: 1 KB
 */
@Pipe({
    standalone: false,
    name: "fileSize"
})

export class FileSizePipe implements PipeTransform {
    private _decimal: DecimalPipe = new DecimalPipe("en");

    private static UNITS: string[] = ["Bytes", "KB", "MB", "GB", "TB", "PB", "EB", "ZB", "YB"];
    private static EXPECTED_UNITS_NUMBER: number = FileSizePipe.UNITS.length;

    private readonly unitsResKey: string = "general.fileSize.pipe.units";
    private readonly K: number = 1024; // bytes
    private readonly YB: number = Math.pow(this.K, 8); //1208925819614629174706176 bytes

    constructor(private translate: L10nService,
                localeService: LocaleService,
                private _ref: ChangeDetectorRef) {

        this._decimal = new DecimalPipe(localeService.getCurrentLanguage());
    }

    transform(bytes: number = 0, precision: number = 2, smallUnits: boolean = false): any {
        return this.doTransform(this.extractMeasures(FileSizePipe.UNITS, this.translate.getMessage(this.unitsResKey)),
                                bytes, precision, smallUnits);
    }

    private doTransform(units: string[], bytes: number, precision: number = 2, smallUnits: boolean = false): string {
        if ((!bytes && bytes != 0) || !isFinite(bytes) || bytes < 0 || bytes > this.YB) {
            return "?";
        }
        if (bytes == 0) {
            return "0 " + units[0];
        }

        const dm: number = precision || 2;
        const i = Math.floor(Math.log(bytes) / Math.log(this.K));
        const formatted: number = (bytes / Math.pow(this.K, i));
        if (smallUnits) {
            return this._decimal.transform(formatted, "1.0-" + dm) + " " + "<small>" + units[i] + "</small>";
        }
        return this._decimal.transform(formatted, "1.0-" + dm) + " " + units[i];
    }

    private extractMeasures(originalUnits: string[], unitsRes: string): string[] {
        if (unitsRes) {
            const newUnits: string[] = unitsRes.split(",");
            if (newUnits.length === FileSizePipe.EXPECTED_UNITS_NUMBER) {
                return newUnits.map(item => item.trim());
            } else {
                console.error("Resource general.fileSize.pipe.units is not properly formatted!");
            }
        }
        // fallback to non-localized defaults
        return originalUnits;
    }
}
